import { useState, useEffect } from "react";
import { DateField } from "@/components/ui/date-field";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AddTermWizard({ open, onOpenChange, tenantId, contract, contracts, onSaved }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ contract_id: "", period: "", count: 1, due_date: "", amount: "", grace_days: 0, followup_date: "", comments: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const options = (contracts && contracts.length ? contracts : (contract ? [contract] : []));
  const kindLabel = (k) => ({ huur: "Huur", lening: "Lening", factuur: "Factuur" }[k] || k);
  const selected = options.find((o) => o.id === form.contract_id) || contract || options[0];

  useEffect(() => {
    if (open) {
      const now = new Date();
      const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const first = (contracts && contracts.length ? contracts : (contract ? [contract] : []))[0];
      const day = Math.min(first?.payment_day || 1, 28);
      setForm({ contract_id: first?.id || "", period, count: 1, due_date: `${period}-${String(day).padStart(2, "0")}`, amount: first?.monthly_rent || "", grace_days: 0, followup_date: "", comments: "" });
      setStep(0);
    }
  }, [open, contract, contracts]);

  const finish = async () => {
    setLoading(true);
    try {
      const count = Math.max(1, Number(form.count) || 1);
      if (count > 1) {
        await api.post("/payments/batch", {
          tenant_id: tenantId, contract_id: form.contract_id || "", start_period: form.period,
          count, amount: Number(form.amount) || 0, payment_day: selected?.payment_day || 1, grace_days: Number(form.grace_days) || 0,
        });
      } else {
        const status = form.due_date < new Date().toISOString().slice(0, 10) ? "late" : "open";
        await api.post("/payments", {
          tenant_id: tenantId, contract_id: form.contract_id || "", period: form.period, due_date: form.due_date,
          amount: Number(form.amount) || 0, status, grace_days: Number(form.grace_days) || 0,
          followup_date: form.followup_date, comments: form.comments,
        });
      }
      toast.success(count > 1 ? `${count} termijnen toegevoegd` : "Termijn toegevoegd");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error("Toevoegen mislukt"); }
    finally { setLoading(false); }
  };

  return (
    <WizardShell open={open} onOpenChange={onOpenChange}
      title="Termijn(en) toevoegen"
      subtitle="Voeg één of meerdere termijnen tegelijk toe"
      steps={["Termijnen", "Details"]}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={step !== 0 || (!!form.contract_id && !!form.period && !!form.amount)}
      finishLabel="Toevoegen">
      {step === 0 && (
        <div className="space-y-4">
          <div>
            <Label>Overeenkomst</Label>
            <div className="mt-1"><Combobox testId="term-contract-select" value={form.contract_id} onChange={(v) => { const o = options.find((x) => x.id === v); set("contract_id", v); if (o) { set("amount", o.monthly_rent || form.amount); const p = form.period; set("due_date", `${p}-${String(Math.min(o.payment_day || 1, 28)).padStart(2, "0")}`); } }} placeholder="Kies overeenkomst" searchPlaceholder="Zoek overeenkomst…"
              options={options.map((o) => ({ value: o.id, label: `${kindLabel(o.kind)}${o.ovk_number ? ` · ${o.ovk_number}` : ""} · start ${o.start_date}${o.monthly_rent ? ` · € ${o.monthly_rent}/mnd` : ""}` }))} /></div>
            <p className="text-xs text-slate-400 mt-1">Kies op welke overeenkomst deze termijn(en) betrekking hebben.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Startperiode (JJJJ-MM)</Label><Input data-testid="term-period-input" value={form.period} onChange={(e) => set("period", e.target.value)} placeholder="2026-07" /></div>
            <div><Label>Aantal termijnen</Label><Input type="number" min={1} data-testid="term-count-input" value={form.count} onChange={(e) => set("count", e.target.value)} /></div>
          </div>
          {Number(form.count) <= 1 && <div><Label>Vervaldatum</Label><DateField testId="term-due-input" value={form.due_date} onChange={(v) => set("due_date", v)} /></div>}
          <div><Label>Bedrag per termijn (€)</Label><Input type="number" data-testid="term-amount-input" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
          {Number(form.count) > 1 && <p className="text-xs text-slate-500">Er worden {form.count} opeenvolgende maandtermijnen aangemaakt vanaf {form.period}.</p>}
        </div>
      )}
      {step === 1 && (
        <div className="space-y-4">
          <div><Label>Grace-periode (dagen)</Label><Input type="number" min={0} data-testid="term-grace-input" value={form.grace_days} onChange={(e) => set("grace_days", e.target.value)} /></div>
          {Number(form.count) <= 1 && <>
            <div><Label>Opvolgdatum (na afspraak)</Label><DateField testId="term-followup-input" value={form.followup_date} onChange={(v) => set("followup_date", v)} /></div>
            <div><Label>Eigen opmerking / comment</Label><Textarea rows={3} data-testid="term-comments-input" value={form.comments} onChange={(e) => set("comments", e.target.value)} /></div>
          </>}
        </div>
      )}
    </WizardShell>
  );
}
